import React, { createContext, useCallback, useContext, useState } from 'react';
import { MainSource } from '../../../constants/Source';
import { createHOCWithHookInside } from '../createHOCWithHookInside';
import { MediaManagerSDKFile } from '../../media-manager-sdk';
import { useAdapter } from '../../hooks/useAdapter';
import { useMMGRSettings } from '../withMMGRSettings';
import { noop } from 'ramda-adjunct';
import { RelatedItemsContextType, RelatedItemsMetaInfo, RelatedItemState } from './withRelatedItems.types';

const MAX_SIZE = 20;

const DEFAULT_ACTIVE_RELATED_ITEMS_STATE = null;

const RelatedItemsContext = createContext<RelatedItemsContextType>({
  isRelatedItemsLoading: false,
  relatedItems: [],
  fetchRelatedItems: ({ fileName, folderId, loadNextItems }) => Promise.resolve(),
  resetRelatedItems: noop,
  hasNextRelatedItems: false,
  isRelatedItemsError: false,
  activeRelatedItemsState: DEFAULT_ACTIVE_RELATED_ITEMS_STATE,
  setActiveRelatedItemsState: noop,
  resetActiveRelatedItemsState: noop,
  relatedItemsMetaInfo: {},
});

export const RelatedItemsProvider = ({ children }) => {
  const [relatedItemsStatus, setRelatedItemsStatus] = useState({
    isLoading: false,
    isError: false,
  });
  const [relatedItemsMetaInfo, setRelatedItemsMetaInfo] = useState<RelatedItemsMetaInfo>({});
  const [relatedItems, setRelatedItems] = useState<MediaManagerSDKFile[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [activeRelatedItemsState, setActiveRelatedItemsState] = useState<RelatedItemState>(
    DEFAULT_ACTIVE_RELATED_ITEMS_STATE,
  );

  const {
    mmgrSettings: { siteMediaToken },
  } = useMMGRSettings();
  const adapter = useAdapter(MainSource.MediaFromWix, siteMediaToken);

  const resetRelatedItems = useCallback(() => {
    setRelatedItems([]);
    setCursor(null);
    setRelatedItemsStatus({ isError: false, isLoading: false });
  }, []);

  const getMetaInfo = (items: MediaManagerSDKFile[]) => {
    const result = items.reduce((metaInfo, item, index) => {
      metaInfo[item.id] = {
        sectionName: 'similar_images',
        itemIndexWithinCategory: index + 1,
      };
      return metaInfo;
    }, {} as RelatedItemsMetaInfo);

    return result;
  };

  const fetchRelatedItems = useCallback(
    async ({ fileName, folderId, loadNextItems = false, mediaType }) => {
      if (loadNextItems && !cursor) {
        setRelatedItemsStatus({ isError: true, isLoading: false });

        throw new Error('Related Items lazy load - no cursor provided');
      }

      try {
        setRelatedItemsStatus({ isError: false, isLoading: true });

        const requestOptions = {
          relatedSource: fileName,
          mediaType,
          paging: {
            size: MAX_SIZE,
            ...(loadNextItems ? { cursor } : {}),
          },
        };

        const response = await adapter.items.searchV2(folderId, '', requestOptions);

        setCursor(response.paging?.cursor);

        if (loadNextItems) {
          setRelatedItemsMetaInfo(getMetaInfo([...relatedItems, ...response.data]));
          setRelatedItems((prev) => [...prev, ...response.data]);
        } else {
          setRelatedItemsMetaInfo(getMetaInfo(response.data));
          setRelatedItems(response.data);
        }

        setRelatedItemsStatus({ isError: false, isLoading: false });
      } catch (error) {
        setRelatedItemsStatus({ isError: true, isLoading: false });
        throw error;
      }
    },
    [adapter.items, cursor, relatedItems],
  );

  const resetActiveRelatedItemsState = () => setActiveRelatedItemsState(DEFAULT_ACTIVE_RELATED_ITEMS_STATE);

  return (
    <RelatedItemsContext.Provider
      value={
        {
          isRelatedItemsLoading: relatedItemsStatus.isLoading,
          relatedItems,
          fetchRelatedItems,
          resetRelatedItems,
          hasNextRelatedItems: !!cursor,
          isRelatedItemsError: relatedItemsStatus.isError,
          activeRelatedItemsState,
          setActiveRelatedItemsState,
          resetActiveRelatedItemsState,
          relatedItemsMetaInfo,
        } as RelatedItemsContextType
      }
    >
      {children}
    </RelatedItemsContext.Provider>
  );
};

export const useRelatedItems = () => useContext(RelatedItemsContext);

export const withRelatedItems = createHOCWithHookInside('WithRelatedItems', useRelatedItems);
